import { useMemo, useState } from 'react';

import { Link } from '@tanstack/react-router';

import type { Contact as ApiContact } from '@/api/generated';
import { auditLogsApi } from '@/api/queries/auditLogs';
import { useContactFieldSuggestions } from '@/api/queries/contactFieldSuggestions';
import { contactsApi } from '@/api/queries/contacts';
import { customFieldsApi } from '@/api/queries/workspace';
import type { Contact } from '@/api/types/contacts';
import {
  ActivityItem,
  Avatar,
  Button,
  Icon,
  IconButton,
  Input,
  SideCard,
  SideRow,
  Tag,
  TagMore,
  TagStack,
  colorFromKey,
  useToast,
} from '@/components/ui';

import { actionMeta } from '@/routes/audit-logs/-components/auditUtils';

import { GeneralNoteEditor } from '../GeneralNoteEditor';
import { ReviewSection } from './sections/ReviewSection';
import { StatusSection } from './sections/StatusSection';
import type { SectionFormProp } from './sections/registry';

interface SideProps {
  contact: Contact | undefined;
  form: SectionFormProp;
}

export function ContactDetailSide({ contact, form }: SideProps) {
  return (
    <aside className="detail-side">
      <SideCard
        title="About this record"
        action={<IconButton icon="moreH" label="More" size="sm" iconSize={13} />}
      >
        <SideRow label="Created" value={formatDate(contact?.audit?.createdAt) ?? '—'} />
        <SideRow label="Last updated" value={formatDate(contact?.audit?.updatedAt) ?? '—'} />
        <SideRow label="Owner" value={contact?.audit?.updatedBy ?? '—'} />
        <SideRow label="ID" value={contact?.id ? shortId(contact.id) : '—'} mono />
        <SideRow label="Version" value={contact?.audit?.version ?? '—'} mono />
      </SideCard>

      <SourcesCard contact={contact} />

      <CustomFieldsCard contact={contact} />

      {contact?.id && <ReviewSection contact={contact} />}

      <StatusSection form={form} />

      {contact?.id && (
        <SideCard title="General note">
          <GeneralNoteEditor contactId={contact.id} initialText={contact.generalNote} />
        </SideCard>
      )}

      <RecentActivityCard contact={contact} />

      <SideCard
        title="Audit trail"
        icon={<Icon name="shield" size={13} />}
        style={{ background: 'var(--brand-soft)', borderColor: 'rgba(31,71,171,0.18)' }}
        titleStyle={{ color: 'var(--brand-ink)' }}
      >
        <div style={{ fontSize: 12, color: 'var(--brand-ink)', marginBottom: 8 }}>
          All edits to this record are logged for GDPR compliance.
        </div>
        <Link
          to="/audit-logs"
          search={{ page: 0, pageSize: 25, q: contact?.id ?? '' }}
          style={{ display: 'block', width: '100%' }}
        >
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Icon name="history" size={12} />}
            style={{ width: '100%', justifyContent: 'center' }}
          >
            View full audit log
          </Button>
        </Link>
      </SideCard>
    </aside>
  );
}

function SourcesCard({ contact }: { contact: Contact | undefined }) {
  const update = contactsApi.useUpdate();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');

  // Suggestions are best-effort, curated via the contact.sources counter table:
  // if it doesn't exist the query errors silently and we just render no
  // <option>s — free text still works.
  const { suggestions: allSuggestions } = useContactFieldSuggestions('sources');

  const sources = useMemo(() => contact?.sources ?? [], [contact?.sources]);
  const suggestions = useMemo(
    () => allSuggestions.filter((s) => !sources.includes(s)),
    [allSuggestions, sources],
  );

  const disabled = !contact?.id || update.isPending;

  // Persist the full contact with the new sources list. We send the contact the
  // server already gave us (Dates serialize to ISO just like the bulk form save
  // in ContactForm) plus the overridden sources, guarded by the current etag.
  const persist = async (next: string[]) => {
    if (!contact?.id) return;
    const etag = contact.audit?.version ? `"${contact.audit.version}"` : `"1"`;
    await update.mutateAsync({
      id: contact.id,
      contact: { ...contact, sources: next } as unknown as ApiContact,
      etag,
    });
  };

  const add = async (raw: string) => {
    const v = raw.trim();
    if (!v) return;
    if (sources.includes(v)) {
      setValue('');
      setAdding(false);
      return;
    }
    try {
      await persist([...sources, v]);
      setValue('');
      setAdding(false);
    } catch (e) {
      toast.show({
        severity: 'error',
        title: 'Failed to add source',
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const remove = async (src: string) => {
    try {
      await persist(sources.filter((s) => s !== src));
    } catch (e) {
      toast.show({
        severity: 'error',
        title: 'Failed to remove source',
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <SideCard
      title="Sources"
      action={
        <IconButton
          icon="plus"
          label="Add source"
          size="sm"
          iconSize={13}
          disabled={disabled}
          onClick={() => setAdding((a) => !a)}
        />
      }
    >
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <Input
            list="source-suggestions"
            autoFocus
            value={value}
            placeholder="Add a source…"
            onChange={(e) => setValue(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void add(value);
              } else if (e.key === 'Escape') {
                setValue('');
                setAdding(false);
              }
            }}
          />
          <datalist id="source-suggestions">
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <IconButton
            icon="check"
            label="Save source"
            size="sm"
            iconSize={13}
            disabled={!value.trim() || disabled}
            onClick={() => void add(value)}
          />
        </div>
      )}
      {sources.length > 0 ? (
        sources.map((src) => (
          <div key={src} className="side-row" style={{ alignItems: 'center', gap: 8 }}>
            <Avatar initials={src.charAt(0).toUpperCase()} color={colorFromKey(src)} size={22} />
            <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-2)' }}>{src}</span>
            <IconButton
              icon="x"
              label={`Remove ${src}`}
              size="sm"
              iconSize={12}
              variant="danger"
              disabled={disabled}
              onClick={() => void remove(src)}
            />
          </div>
        ))
      ) : (
        !adding && <div style={{ color: 'var(--text-4)', fontSize: 12.5 }}>No sources linked</div>
      )}
    </SideCard>
  );
}

const CUSTOM_FIELD_CHIPS = 20;

function CustomFieldsCard({ contact }: { contact: Contact | undefined }) {
  const { data: definitions = [] } = customFieldsApi.useList();
  const fields = useMemo(() => {
    const names = new Map(
      definitions.filter((d) => d.key).map((d) => [d.key as string, d.name] as const),
    );
    return Object.entries(contact?.customFields ?? {})
      .filter(([, values]) => values.length > 0)
      .map(([key, values]) => ({ key, label: names.get(key) ?? key, values }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [contact?.customFields, definitions]);

  if (fields.length === 0) return null;

  return (
    <SideCard title="Custom fields">
      {fields.map((field) => (
        <div
          key={field.key}
          style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}
        >
          <span style={{ fontSize: 12, color: 'var(--text-3)' }} title={field.key}>
            {field.label}
          </span>
          <TagStack>
            {field.values.slice(0, CUSTOM_FIELD_CHIPS).map((value) => (
              <Tag key={value}>{value}</Tag>
            ))}
            {field.values.length > CUSTOM_FIELD_CHIPS && (
              <TagMore count={field.values.length - CUSTOM_FIELD_CHIPS} />
            )}
          </TagStack>
        </div>
      ))}
    </SideCard>
  );
}

function RecentActivityCard({ contact }: { contact: Contact | undefined }) {
  // The audit list is a Suspense query; only call it once we have an id.
  // Falls back to the contact's own audit metadata for the create flow.
  if (!contact?.id) {
    return (
      <SideCard title="Recent activity">
        <div style={{ color: 'var(--text-4)', fontSize: 12.5 }}>
          Activity appears once the record is saved.
        </div>
      </SideCard>
    );
  }
  return <RecentActivityList contactId={contact.id} contact={contact} />;
}

function RecentActivityList({ contactId, contact }: { contactId: string; contact: Contact }) {
  const { data } = auditLogsApi.useList({ contactId, page: 0, pageSize: 5 });
  const events = data.data ?? [];

  if (events.length === 0) {
    return (
      <SideCard title="Recent activity">
        {contact.audit?.createdAt ? (
          <ActivityItem
            tone="amber"
            title="Record created"
            meta={formatDate(contact.audit.createdAt) ?? ''}
          />
        ) : (
          <div style={{ color: 'var(--text-4)', fontSize: 12.5 }}>No recent activity</div>
        )}
      </SideCard>
    );
  }

  return (
    <SideCard title="Recent activity">
      {events.map((event) => {
        const meta = actionMeta(event.change?.action);
        const tone =
          meta.label === 'Created'
            ? 'amber'
            : meta.label === 'Soft-deleted'
            ? 'amber'
            : meta.label === 'Synced'
            ? 'green'
            : 'brand';
        return (
          <ActivityItem
            key={event.id}
            tone={tone as 'brand' | 'green' | 'amber'}
            title={event.change?.summary || meta.label}
            meta={`${formatDate(event.occurredAt) ?? ''}${
              event.actor?.id ? ` · ${event.actor.id}` : ''
            }`}
          />
        );
      })}
    </SideCard>
  );
}

function formatDate(value?: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}
